import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import type { ShellLink } from "@/lib/surfaces"

interface FoundationLayoutProps {
  children?: ReactNode
  description: string
  links: readonly ShellLink[]
  showLinks?: boolean
  title: string
}

export function FoundationLayout({
  children,
  description,
  links,
  showLinks = true,
  title,
}: FoundationLayoutProps) {
  return (
    <div className="min-h-svh px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <section className="rounded-4xl border border-border/70 px-5 py-5 sm:px-7 sm:py-7">
          <div className="flex flex-col gap-5">
            <div className="flex max-w-3xl flex-col gap-3">
              <h1 className="font-heading text-4xl leading-tight tracking-tight text-balance sm:text-5xl">
                {title}
              </h1>
              <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
                {description}
              </p>
            </div>
          </div>
          {showLinks ? (
            <>
              <Separator className="my-6" />
              <nav className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                {links.map((link) => (
                  <Button
                    key={link.to}
                    asChild
                    variant="outline"
                    className="h-auto justify-start whitespace-normal px-4 py-3 text-left"
                  >
                    <Link to={link.to}>
                      <span className="flex flex-col gap-1">
                        <span className="font-medium">{link.label}</span>
                        <span className="text-sm leading-6 text-muted-foreground">
                          {link.description}
                        </span>
                      </span>
                    </Link>
                  </Button>
                ))}
              </nav>
            </>
          ) : null}
        </section>

        {children ?? null}
      </div>
    </div>
  )
}
